/**
 * OMMI quarterly season drop dates (CET).
 * Launches at 00:00 CET on: March 21, June 21, September 22, December 21.
 * Repeats every year. See .cursor/.agents/ommi-style-director.md.
 */

export const SEASON_LAUNCH_CET = [
  { month: 3, day: 21 },   // Season 1 — Spring
  { month: 6, day: 21 },   // Season 2 — Summer
  { month: 9, day: 22 },   // Season 3 — Autumn
  { month: 12, day: 21 },  // Season 4 — Winter
] as const

export type SeasonIndex = 1 | 2 | 3 | 4

const CET_TZ = 'Europe/Paris'

/**
 * Get calendar date parts (year, month, day) for a given instant in CET/CEST (Europe/Paris).
 */
export function getCETDateParts(date: Date): { year: number; month: number; day: number } {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: CET_TZ,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })
  const parts = formatter.formatToParts(date)
  const year = Number(parts.find((p) => p.type === 'year')?.value ?? date.getUTCFullYear())
  const month = Number(parts.find((p) => p.type === 'month')?.value ?? date.getUTCMonth() + 1)
  const day = Number(parts.find((p) => p.type === 'day')?.value ?? date.getUTCDate())
  return { year, month, day }
}

/**
 * Return a Date (UTC) for 00:00 in Europe/Paris on the given year and season (1–4).
 * Uses CET (UTC+1) for winter/spring and CEST (UTC+2) for summer/autumn.
 */
export function getSeasonLaunchUTC(year: number, seasonIndex: SeasonIndex): Date {
  const { month, day } = SEASON_LAUNCH_CET[seasonIndex - 1]
  // 00:00 Paris: CET = UTC+1 → 23:00 UTC prev day; CEST = UTC+2 → 22:00 UTC prev day
  const isSummer = month >= 4 && month <= 10
  const utcHour = isSummer ? 22 : 23
  return new Date(Date.UTC(year, month - 1, day - 1, utcHour, 0, 0, 0))
}

/**
 * Which season (1–4) contains the given CET date parts.
 * Seasons: 1 = Mar21–Jun21, 2 = Jun21–Sep22, 3 = Sep22–Dec21, 4 = Dec21–Mar21.
 */
export function getSeasonIndexFromCETParts(parts: { year: number; month: number; day: number }): SeasonIndex {
  const { month, day } = parts
  if (month < 3 || (month === 3 && day < 21)) return 4
  if (month < 6 || (month === 6 && day < 21)) return 1
  if (month < 9 || (month === 9 && day < 22)) return 2
  if (month < 12 || (month === 12 && day < 21)) return 3
  return 4
}

/**
 * Current season (1–4) based on "now" in CET.
 */
export function getCurrentSeasonIndex(now: Date = new Date()): SeasonIndex {
  const parts = getCETDateParts(now)
  return getSeasonIndexFromCETParts(parts)
}

/**
 * Next season launch (00:00 CET/CEST) as a Date.
 * When in Winter (season 4): next is Spring (Mar 21) — same year if we're in Jan–Mar, next year if we're in Dec.
 */
export function getNextSeasonLaunchCET(now: Date = new Date()): Date {
  const parts = getCETDateParts(now)
  const current = getSeasonIndexFromCETParts(parts)
  const nextSeason: SeasonIndex = (current === 4 ? 1 : ((current + 1) as SeasonIndex))
  const nextYear =
    current === 4
      ? (parts.month === 12 ? parts.year + 1 : parts.year) // Dec → next year; Jan–Mar → this year
      : parts.year
  return getSeasonLaunchUTC(nextYear, nextSeason)
}

/**
 * Human-readable period for a season in a given year.
 * @param locale - BCP 47 locale (e.g. 'en', 'de') for month names; defaults to 'en'.
 */
export function getSeasonPeriodLabel(
  seasonIndex: SeasonIndex,
  year: number,
  locale: string = 'en'
): string {
  const start = SEASON_LAUNCH_CET[seasonIndex - 1]
  const endIndex = seasonIndex === 4 ? 0 : seasonIndex
  const end = SEASON_LAUNCH_CET[endIndex]
  const endYear = seasonIndex === 4 ? year + 1 : year
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'short' })
  const startMonth = monthFormatter.format(new Date(year, start.month - 1, 1))
  const endMonth = monthFormatter.format(new Date(endYear, end.month - 1, 1))
  return `${startMonth} ${year} – ${endMonth} ${endYear}`
}
